/**
 * Integrations Page - Professional Outer Realm Interface
 * 
 * Global integration management for professional automation.
 * This page operates in the Outer Realm with professional styling and business focus.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { motion } from 'framer-motion';
import { 
  Search,
  Zap,
  Clock,
  BarChart3,
  Plus,
  Sparkles,
  Settings,
  CheckCircle,
  Info,
  Unplug,
  Activity,
} from 'lucide-react';
import { useSpace } from '../context/SpaceContext';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

import { integrationRegistry, getIntegration } from '../integrations/registry';
import { IntegrationDefinition, IntegrationCategory, IntegrationStatus } from '../integrations/types';
import ComingSoonModal from '../components/integrations/shared/ComingSoonModal';
import ClaudeDesktopIntegrationModal from '../components/integrations/claude/ClaudeDesktopIntegrationModal';
import CustomGPTIntegrationModal from '../components/integrations/openai/CustomGPTIntegrationModal';
import {
  WhatsAppIntegrationModal,
  toWhatsAppPlanTier,
} from '../components/integrations/whatsapp/WhatsAppIntegrationModal';
import { XBookmarksIntegrationModal } from '../components/integrations/x_bookmarks/XBookmarksIntegrationModal';
import { useCriticalAnalytics } from '../utils/criticalAnalytics';
import ChromeExtensionIntegrationModal from '../components/integrations/chrome/ChromeExtensionIntegrationModal';
import { ChromeWebStoreSetupPanel } from '../components/integrations/chrome/ChromeWebStoreSetupPanel';
import BareAPIIntegrationModal from '../components/integrations/bare_api/BareAPIIntegrationModal';
import BareMCPIntegrationModal from '../components/integrations/bare_mcp/BareMCPIntegrationModal';
import CursorIntegrationModal from '../components/integrations/cursor/CursorIntegrationModal';
import N8NIntegrationModal from '../components/integrations/n8n/N8NIntegrationModal';
import DifyIntegrationModal from '../components/integrations/dify/DifyIntegrationModal';
import ZapierIntegrationModal from '../components/integrations/zapier/ZapierIntegrationModal';
import IntegrationIcon from '../components/integrations/shared/IntegrationIcon';
import { FeatureSupportMatrix } from '../components/integrations/FeatureSupportMatrix';
import { apiService, SpaceSocket } from '../services/api';
import { configurationService } from '../integrations/services/ConfigurationService';
import { IntegrationConfigurationModal } from '../components/integrations/shared/IntegrationConfigurationModal';
import { useConnectedBindingsHealth } from '../hooks/useConnectedBindingsHealth';
import {
  deriveIntegrationBindingLifecycle,
  integrationBindingNeedsAttention,
  type IntegrationBindingLifecycle,
} from '../utils/integrationBindingActivity';
import { integrationSupportsAudioContributeOrLimited } from '../integrations/capabilities/memoryModalities';
import MemoryTypesBadges from '../components/ui/MemoryTypesBadges';

interface ConnectedIntegration {
  integrationId: string;
  name: string;
  status: 'active' | 'inactive';
  bindingStatus: 'pending' | 'active' | 'inactive';
  socketName: string;
  lastUsed: string;
  bindingId: string;
  socketId: string;
}

const LIFECYCLE_BADGE_CLASS: Record<IntegrationBindingLifecycle, string> = {
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  inactive: 'bg-gray-100 text-gray-700 border-gray-200',
  healthy: 'bg-green-100 text-green-700 border-green-200',
  idle: 'bg-slate-100 text-slate-700 border-slate-200',
  degraded: 'bg-amber-100 text-amber-800 border-amber-200',
  error: 'bg-red-100 text-red-700 border-red-200',
  unknown: 'bg-slate-50 text-slate-600 border-slate-200',
};

export default function IntegrationsPage() {
  const { availableSpaces, currentSpace } = useSpace();
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const criticalAnalytics = useCriticalAnalytics();
  const xOAuthTrackedBindingRef = useRef<string | null>(null);

  // Get user's actual subscription tier from auth context
  const backendTier = user?.user_metadata?.tier || 'free';
  
  const userTier = toWhatsAppPlanTier(backendTier);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<IntegrationCategory | 'all'>('all');
  const [selectedStatus, setSelectedStatus] = useState<IntegrationStatus | 'all'>('available');
  const [memoryTypeFilter, setMemoryTypeFilter] = useState<'all' | 'audio_supported'>('all');
  const [selectedIntegration, setSelectedIntegration] = useState<IntegrationDefinition | null>(null);
  const [showComingSoonModal, setShowComingSoonModal] = useState(false);
  const [showIntegrationConfigModal, setShowIntegrationConfigModal] = useState(false);
  
  // Configuration modal state
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [configIntegrationId, setConfigIntegrationId] = useState<string>('');
  const [configIntegrationData, setConfigIntegrationData] = useState<any>(null);
  
  // Connected integrations state
  const [connectedIntegrations, setConnectedIntegrations] = useState<ConnectedIntegration[]>([]);
  const [sockets, setSockets] = useState<SpaceSocket[]>([]);
  const [loadingConnected, setLoadingConnected] = useState(false);
  const [attentionFilter, setAttentionFilter] = useState<'all' | 'attention'>('all');

  // Get the current space ID - use current space or first available space
  const currentSpaceId = currentSpace?.space_id || availableSpaces[0]?.space_id;

  const {
    bindingHealthById,
    windowHours: bindingHealthWindowHours,
    loading: bindingHealthLoading,
    error: bindingHealthError,
    refetch: refetchBindingHealth,
  } = useConnectedBindingsHealth(currentSpaceId, connectedIntegrations);

  const showChromeWebStoreSetup =
    searchParams.get('setup') === 'chrome-extension' && Boolean(currentSpaceId);

  const dismissChromeWebStoreSetup = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('setup');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const xConnected = searchParams.get('x_connected');
    const xOAuthError = searchParams.get('x_oauth_error');
    if (xOAuthError) {
      toast.error(decodeURIComponent(xOAuthError));
      const next = new URLSearchParams(searchParams);
      next.delete('x_oauth_error');
      setSearchParams(next, { replace: true });
      return;
    }
    if (xConnected && currentSpaceId) {
      if (xOAuthTrackedBindingRef.current !== xConnected) {
        xOAuthTrackedBindingRef.current = xConnected;
        criticalAnalytics.trackIntegrationConnected('twitter_bookmarks');
      }
      const integrationDef = integrationRegistry.twitter_bookmarks;
      setSelectedIntegration(integrationDef);
      setConfigIntegrationData({
        bindingId: xConnected,
        binding: { binding_id: xConnected },
        socket: null,
      });
      setShowIntegrationConfigModal(true);
      const next = new URLSearchParams(searchParams);
      next.delete('x_connected');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, currentSpaceId, criticalAnalytics]);

  // Debug logging for modal rendering conditions
  useEffect(() => {
    if (selectedIntegration?.id === 'claude_desktop') {
      console.log('🎯 Claude Desktop modal conditions:', {
        integrationId: selectedIntegration?.id,
        showModal: showIntegrationConfigModal,
        hasSpaceId: !!currentSpaceId,
        spaceId: currentSpaceId,
        shouldRender: selectedIntegration?.id === 'claude_desktop' && showIntegrationConfigModal && currentSpaceId
      });
    }
  }, [selectedIntegration?.id, showIntegrationConfigModal, currentSpaceId]);

  // Fetch connected integrations
  const loadConnectedIntegrations = useCallback(async () => {
    if (!currentSpaceId) return;
    
    try {
      setLoadingConnected(true);
      const socketList = await apiService.getSocketsForSpace(currentSpaceId);
      setSockets(socketList.sockets);
      
      // Process socket data to extract integration bindings
      const connected: ConnectedIntegration[] = [];

      for (const socket of socketList.sockets) {
        for (const binding of socket.integration_bindings || []) {
          let bindingStatus: 'pending' | 'active' | 'inactive' = 'inactive';
          if (binding.status === 'pending') {
            bindingStatus = 'pending';
          } else if (binding.status === 'inactive' || !binding.is_active) {
            bindingStatus = 'inactive';
          } else {
            bindingStatus = 'active';
          }
          const rowStatus: 'active' | 'inactive' =
            bindingStatus === 'active' ? 'active' : 'inactive';
          connected.push({
            integrationId: binding.integration_type,
            name: binding.integration_name,
            status: rowStatus,
            bindingStatus,
            socketName: socket.socket_name,
            lastUsed: binding.last_used || 'Never',
            bindingId: binding.binding_id,
            socketId: socket.socket_id,
          });
        }
      }

      setConnectedIntegrations(connected);
    } catch (err) {
      console.error('Failed to load connected integrations:', err);
    } finally {
      setLoadingConnected(false);
    }
  }, [currentSpaceId]);

  useEffect(() => {
    if (currentSpaceId) {
      loadConnectedIntegrations();
    }
  }, [currentSpaceId, loadConnectedIntegrations]);

  const lifecycleCounts = useMemo(() => {
    const acc: Record<IntegrationBindingLifecycle, number> = {
      pending: 0,
      inactive: 0,
      healthy: 0,
      idle: 0,
      degraded: 0,
      error: 0,
      unknown: 0,
    };
    for (const c of connectedIntegrations) {
      const health = bindingHealthById[c.bindingId];
      const life = deriveIntegrationBindingLifecycle({
        bindingIsActive: c.status === 'active',
        bindingStatus: c.bindingStatus,
        health,
        healthFetchFailed: Boolean(bindingHealthError),
      });
      acc[life] += 1;
    }
    return acc;
  }, [connectedIntegrations, bindingHealthById, bindingHealthError]);

  const displayedConnectedIntegrations = useMemo(() => {
    if (attentionFilter === 'all') {
      return connectedIntegrations;
    }
    return connectedIntegrations.filter((c) => {
      const health = bindingHealthById[c.bindingId];
      const life = deriveIntegrationBindingLifecycle({
        bindingIsActive: c.status === 'active',
        bindingStatus: c.bindingStatus,
        health,
        healthFetchFailed: Boolean(bindingHealthError),
      });
      return integrationBindingNeedsAttention(life);
    });
  }, [
    attentionFilter,
    connectedIntegrations,
    bindingHealthById,
    bindingHealthError,
  ]);

  // Get all integrations from registry
  const allIntegrations = Object.values(integrationRegistry);

  // Filter integrations
  const filteredIntegrations = allIntegrations.filter((integration) => {
    const matchesSearch =
      integration.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      integration.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory =
      selectedCategory === 'all' || integration.category === selectedCategory;
    const matchesStatus =
      selectedStatus === 'all' || integration.status === selectedStatus;
    const matchesMemory =
      memoryTypeFilter === 'all' ||
      (memoryTypeFilter === 'audio_supported' &&
        integrationSupportsAudioContributeOrLimited(integration.id));

    return matchesSearch && matchesCategory && matchesStatus && matchesMemory;
  });

  // Group integrations by status
  const integrationsByStatus = {
    available: filteredIntegrations.filter(i => i.status === 'available'),
    release_1_4: filteredIntegrations.filter(i => i.status === 'release_1_4'),
    in_progress: filteredIntegrations.filter(i => i.status === 'in_progress'),
    in_development: filteredIntegrations.filter(i => i.status === 'in_development'),
    coming_soon: filteredIntegrations.filter(i => i.status === 'coming_soon'),
    concept: filteredIntegrations.filter(i => i.status === 'concept')
  };

  const handleIntegrationClick = (integration: IntegrationDefinition) => {
    console.log('🔌 Connect button clicked:', {
      integrationId: integration.id,
      integrationName: integration.name,
      status: integration.status,
      currentSpaceId: currentSpaceId,
      hasSpaceId: !!currentSpaceId
    });
    
    setSelectedIntegration(integration);
    
    if (integration.status === 'available') {
      // Check if we have a valid space ID before opening integration modal
      if (!currentSpaceId) {
        console.warn('⚠️ No space ID available, cannot open integration modal');
        alert(t('page.spaceRequiredAlert', { ns: 'integrations' }) as string);
        return;
      }
      // Always open integration modal directly - modals will handle socket internally
      console.log('✅ Opening integration config modal for:', integration.id);
      setConfigIntegrationData(null);
      setShowIntegrationConfigModal(true);
    } else {
      console.log('📅 Opening coming soon modal for:', integration.id);
      setShowComingSoonModal(true);
    }
  };

  const formatLastUsed = (lastUsed: string) => {
    if (lastUsed === 'Never' || !lastUsed) return null;
    
    try {
      const date = new Date(lastUsed);
      const now = new Date();
      
      // Validate date is valid
      if (isNaN(date.getTime())) {
        return null;
      }
      
      const diffMs = now.getTime() - date.getTime();
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      
      // Guard against future dates (negative time differences)
      if (diffDays < 0) {
        return null;
      }
      
      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
      return `${Math.floor(diffDays / 30)} months ago`;
    } catch {
      return null;
    }
  };

  const handleConfigureIntegration = (integration: ConnectedIntegration) => {
    // Check if this integration supports configuration using the service
    if (configurationService.supportsConfiguration(integration.integrationId)) {
      // Special handling for WhatsApp integration - use the specific modal
      if (integration.integrationId === 'whatsapp_basic') {
        const integrationDef = integrationRegistry['whatsapp_basic'];
        setSelectedIntegration(integrationDef);
        setShowIntegrationConfigModal(true);
        // Store the existing integration data for the modal
        setConfigIntegrationData({
          bindingId: integration.bindingId,
          binding: sockets.find(s => s.socket_id === integration.socketId)?.integration_bindings.find(b => b.binding_id === integration.bindingId),
          socket: sockets.find(s => s.socket_id === integration.socketId)
        });
        return;
      }

      if (integration.integrationId === 'twitter_bookmarks') {
        const integrationDef = integrationRegistry.twitter_bookmarks;
        setSelectedIntegration(integrationDef);
        setShowIntegrationConfigModal(true);
        setConfigIntegrationData({
          bindingId: integration.bindingId,
          binding: sockets.find(s => s.socket_id === integration.socketId)?.integration_bindings.find(b => b.binding_id === integration.bindingId),
          socket: sockets.find(s => s.socket_id === integration.socketId)
        });
        return;
      }
      
      // Special handling for Chrome extension integration
      if (integration.integrationId === 'chrome_extension') {
        const integrationDef = integrationRegistry['chrome_extension'];
        setSelectedIntegration(integrationDef);
        setShowIntegrationConfigModal(true);
        // Store the existing integration data for the modal
        setConfigIntegrationData({
          bindingId: integration.bindingId,
          binding: sockets.find(s => s.socket_id === integration.socketId)?.integration_bindings.find(b => b.binding_id === integration.bindingId),
          socket: sockets.find(s => s.socket_id === integration.socketId)
        });
        return;
      }
      
      // Generic configuration modal for other integrations
      setConfigIntegrationId(integration.integrationId);
      setConfigIntegrationData({
        integrationId: integration.integrationId,
        name: integration.name,
        bindingId: integration.bindingId,
        socketId: integration.socketId,
        binding: sockets.find(s => s.socket_id === integration.socketId)?.integration_bindings.find(b => b.binding_id === integration.bindingId),
        socket: sockets.find(s => s.socket_id === integration.socketId)
      });
      setShowConfigModal(true);
    }
  };

  const handleDisconnect = async (integration: ConnectedIntegration) => {
    // Confirm deletion with user
    const confirmed = window.confirm(
      `Are you sure you want to disconnect ${integration.name}?\n\nThis will remove the integration and stop all data synchronization. This action cannot be undone.`
    );
    
    if (!confirmed) {
      return;
    }

    try {
      await apiService.deleteIntegrationBinding(
        integration.socketId, 
        integration.bindingId
      );
      
      // Reload connected integrations after successful deletion
      if (currentSpaceId) {
        loadConnectedIntegrations();
      }
      
      // Show success feedback
      console.log(`Successfully disconnected ${integration.name}`);
      
    } catch (err) {
      console.error('Failed to disconnect integration:', err);
      
      // Show user-friendly error message
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      alert(t('connectedIntegrations.errors.disconnectFailed', { name: integration.name, error: errorMessage, ns: 'integrations' }) as string);
    }
  };

  const getStatusBadge = (status: IntegrationStatus) => {
    const statusConfig = {
      available: { label: t('status.available', { ns: 'integrations' }) as string, className: 'bg-green-100 text-green-700 border-green-200' },
      release_1_4: { label: t('status.release1_4', { ns: 'integrations' }) as string, className: 'bg-blue-100 text-blue-700 border-blue-200' },
      in_progress: { label: t('status.inProgress', { ns: 'integrations' }) as string, className: 'bg-orange-100 text-orange-700 border-orange-200' },
      in_development: { label: t('status.inDevelopment', { ns: 'integrations' }) as string, className: 'bg-amber-100 text-amber-800 border-amber-200' },
      coming_soon: { label: t('status.comingSoon', { ns: 'integrations' }) as string, className: 'bg-purple-100 text-purple-700 border-purple-200' },
      concept: { label: t('status.concept', { ns: 'integrations' }) as string, className: 'bg-gray-100 text-gray-700 border-gray-200' }
    };

    const config = statusConfig[status];
    return (
      <Badge className={`${config.className} border`}>
        {config.label}
      </Badge>
    );
  };

  const getActionButton = (integration: IntegrationDefinition) => {
    const buttonConfig = {
      available: { 
        label: t('actions.connect', { ns: 'integrations' }) as string, 
        icon: <Zap className="w-4 h-4" />,
        className: 'bg-indigo-600 hover:bg-indigo-700 text-white'
      },
      release_1_4: { 
        label: t('actions.notifyMe', { ns: 'integrations' }) as string, 
        icon: <Clock className="w-4 h-4" />,
        className: 'bg-blue-600 hover:bg-blue-700 text-white'
      },
      in_progress: { 
        label: t('actions.getUpdates', { ns: 'integrations' }) as string, 
        icon: <BarChart3 className="w-4 h-4" />,
        className: 'bg-orange-600 hover:bg-orange-700 text-white'
      },
      in_development: {
        label: t('actions.getUpdates', { ns: 'integrations' }) as string,
        icon: <BarChart3 className="w-4 h-4" />,
        className: 'bg-amber-600 hover:bg-amber-700 text-white'
      },
      coming_soon: { 
        label: t('actions.earlyAccess', { ns: 'integrations' }) as string, 
        icon: <Sparkles className="w-4 h-4" />,
        className: 'bg-purple-600 hover:bg-purple-700 text-white'
      },
      concept: { 
        label: t('actions.interested', { ns: 'integrations' }) as string, 
        icon: <Plus className="w-4 h-4" />,
        className: 'bg-gray-600 hover:bg-gray-700 text-white'
      }
    };

    const config = buttonConfig[integration.status];
    return (
      <Button
        onClick={() => handleIntegrationClick(integration)}
        className={`${config.className} shadow-sm`}
        size="sm"
      >
        {config.icon}
        {config.label}
      </Button>
    );
  };

  const renderIntegrationSection = (title: string, integrations: IntegrationDefinition[], count: number) => {
    if (integrations.length === 0) return null;

    return (
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-4"
      >
        <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          {title}
          <Badge variant="secondary" className="bg-slate-100 text-slate-600">
            {count}
          </Badge>
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {integrations.map((integration) => (
            <Card 
              key={integration.id}
              className="hover:shadow-md transition-all duration-200 border-border bg-card"
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg bg-gradient-to-r ${integration.gradient}`}>
                      <IntegrationIcon icon={integration.icon} className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-base text-slate-900">
                        {integration.name}
                      </CardTitle>
                      <div className="mt-1">
                        {getStatusBadge(integration.status)}
                      </div>
                    </div>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="pt-0">
                <p className="text-sm text-slate-600 mb-3 line-clamp-2">
                  {integration.description}
                </p>

                <div className="mb-3" aria-label={t('memoryModalities.headingSelection', { ns: 'integrations' }) as string}>
                  <MemoryTypesBadges integrationId={integration.id} compact className="gap-1" />
                </div>
                
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex flex-wrap gap-1">
                    {integration.socketTypes.slice(0, 2).map((socketType) => (
                      <Badge 
                        key={socketType}
                        variant="outline"
                        className="text-xs bg-slate-50 text-slate-600 border-slate-200"
                      >
                        {socketType.toUpperCase()}
                      </Badge>
                    ))}
                    {integration.socketTypes.length > 2 && (
                      <Badge 
                        variant="outline"
                        className="text-xs bg-slate-50 text-slate-600 border-slate-200"
                      >
                        +{integration.socketTypes.length - 2}
                      </Badge>
                    )}
                  </div>
                  
                  <div className="shrink-0">{getActionButton(integration)}</div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </motion.section>
    );
  };

  return (
    <div className="min-h-0 bg-transparent">
      {showChromeWebStoreSetup && currentSpaceId && (
        <div className="container mx-auto px-6 pt-4">
          <ChromeWebStoreSetupPanel
            spaceId={currentSpaceId}
            onDismiss={dismissChromeWebStoreSetup}
            onBindingsChanged={loadConnectedIntegrations}
          />
        </div>
      )}
      {/* Professional Header */}
      <div className="border-b border-border bg-card/80 shadow-sm backdrop-blur-md">
        <div className="container mx-auto px-6 py-6">
          <div>
            <h1 className="text-2xl font-bold text-foreground">{t('page.title', { ns: 'integrations' })}</h1>
            <p className="text-muted-foreground mt-1">
              {t('page.subtitleNew', { ns: 'integrations' })}
            </p>
            <p className="text-muted-foreground mt-2 text-sm max-w-2xl opacity-90">
              {t('page.descriptionNew', { ns: 'integrations' })}
            </p>
            <div className="sr-only">n8n ai memory, simple memory n8n, mem0 n8n, n8n mem0, mem0 alternative</div>
          </div>
        </div>
      </div>

      {/* Connected Integrations Section */}
      <div className="container mx-auto px-6 py-6">
        {loadingConnected ? (
          <div className="mb-8">
            <div className="text-center py-4">
              <div className="text-slate-400">{t('connectedIntegrations.loading', { ns: 'integrations' })}</div>
            </div>
          </div>
        ) : connectedIntegrations.length > 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <h2 className="text-lg font-semibold text-slate-900">
                  {t('connectedIntegrations.title', { ns: 'integrations' })}
                </h2>
                <Badge variant="secondary" className="bg-slate-100 text-slate-600">
                  {connectedIntegrations.length}
                </Badge>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {bindingHealthLoading && (
                  <span className="text-xs text-muted-foreground">
                    {t('connectedIntegrations.activity.loadingHealth', { ns: 'integrations' })}
                  </span>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="border-slate-200"
                  onClick={() => navigate('/dashboard?view=monitoring')}
                >
                  <Activity className="w-4 h-4 mr-2" />
                  {t('connectedIntegrations.activity.openMonitoring', { ns: 'integrations' })}
                </Button>
              </div>
            </div>

            {bindingHealthError ? (
              <div className="mb-4 flex flex-wrap items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                <span>{t('connectedIntegrations.activity.loadFailed', { ns: 'integrations' })}</span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="border-red-300 bg-white"
                  onClick={() => refetchBindingHealth()}
                >
                  {t('connectedIntegrations.activity.retry', { ns: 'integrations' })}
                </Button>
              </div>
            ) : null}

            <p className="text-xs text-muted-foreground mb-3">
              {t('connectedIntegrations.activity.summary', {
                ns: 'integrations',
                healthy: lifecycleCounts.healthy,
                idle: lifecycleCounts.idle,
                inactive: lifecycleCounts.inactive,
                degraded: lifecycleCounts.degraded,
                error: lifecycleCounts.error,
                unknown: lifecycleCounts.unknown,
                pending: lifecycleCounts.pending,
              })}
            </p>

            <div className="flex flex-wrap gap-2 mb-4">
              <Button
                type="button"
                variant={attentionFilter === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setAttentionFilter('all')}
              >
                {t('connectedIntegrations.activity.filterAll', { ns: 'integrations' })}
              </Button>
              <Button
                type="button"
                variant={attentionFilter === 'attention' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setAttentionFilter('attention')}
              >
                {t('connectedIntegrations.activity.filterNeedsAttention', { ns: 'integrations' })}
              </Button>
            </div>

            {attentionFilter === 'attention' && displayedConnectedIntegrations.length === 0 ? (
              <p className="text-sm text-muted-foreground mb-4">
                {t('connectedIntegrations.activity.filterEmpty', { ns: 'integrations' })}
              </p>
            ) : null}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {displayedConnectedIntegrations.map((integration) => {
                const integrationDef = getIntegration(integration.integrationId);
                const health = bindingHealthById[integration.bindingId];
                const lifecycle = deriveIntegrationBindingLifecycle({
                  bindingIsActive: integration.status === 'active',
                  bindingStatus: integration.bindingStatus,
                  health,
                  healthFetchFailed: Boolean(bindingHealthError),
                });
                return (
                  <Card 
                    key={integration.bindingId}
                    className="hover:shadow-md transition-all duration-200 border-border bg-card"
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          {integrationDef ? (
                            <div className={`p-2 rounded-lg bg-gradient-to-r ${integrationDef.gradient}`}>
                              <IntegrationIcon icon={integrationDef.icon} className="w-6 h-6 text-white" />
                            </div>
                          ) : (
                            <div className="p-2 rounded-lg bg-slate-500">
                              <IntegrationIcon icon="plug" className="w-6 h-6 text-white" />
                            </div>
                          )}
                          <div>
                            <CardTitle className="text-base text-slate-900">
                              {integration.name}
                            </CardTitle>
                            <div className="mt-1 flex flex-wrap gap-1">
                              <Badge
                                className={`border ${LIFECYCLE_BADGE_CLASS[lifecycle]}`}
                              >
                                {t(`connectedIntegrations.activity.lifecycle.${lifecycle}`, {
                                  ns: 'integrations',
                                })}
                              </Badge>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    
                    <CardContent className="pt-0">
                      {(integration.socketName || formatLastUsed(integration.lastUsed)) && (
                        <div className="flex items-center justify-between text-sm text-slate-600 mb-2">
                          {integration.socketName && (
                            <span>{t('connectedIntegrations.socket', { ns: 'integrations' })}: {integration.socketName}</span>
                          )}
                          {formatLastUsed(integration.lastUsed) && (
                            <span>{t('connectedIntegrations.lastUsed', { ns: 'integrations' })}: {formatLastUsed(integration.lastUsed)}</span>
                          )}
                        </div>
                      )}
                      {integration.bindingStatus === 'active' && !bindingHealthError ? (
                        <div className="text-xs text-slate-600 space-y-1 mb-4">
                          <div>
                            <span className="font-medium text-slate-700">
                              {t('connectedIntegrations.activity.lastApiSuccess', { ns: 'integrations' })}
                              {': '}
                            </span>
                            {health?.last_success
                              ? formatDistanceToNow(new Date(health.last_success), {
                                  addSuffix: true,
                                })
                              : t('connectedIntegrations.activity.lastApiSuccessNever', {
                                  ns: 'integrations',
                                })}
                          </div>
                          <div>
                            {t('connectedIntegrations.activity.requestsInWindow', {
                              ns: 'integrations',
                              count: health?.total_events ?? 0,
                              hours: bindingHealthWindowHours,
                            })}
                          </div>
                        </div>
                      ) : null}
                      
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className={`flex-1 border-slate-200 ${
                            configurationService.supportsConfiguration(integration.integrationId)
                              ? 'text-slate-700 hover:border-indigo-300 hover:text-indigo-700'
                              : 'text-slate-400 cursor-not-allowed'
                          }`}
                          disabled={!configurationService.supportsConfiguration(integration.integrationId)}
                          onClick={() => handleConfigureIntegration(integration)}
                          title={configurationService.supportsConfiguration(integration.integrationId)
                            ? `Configure ${integration.name} integration`
                            : 'Configuration not available for this integration'
                          }
                        >
                          <Settings className="w-4 h-4 mr-2" />
                          {t('connectedIntegrations.manage', { ns: 'integrations' })}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-slate-200 text-red-600 hover:text-red-700 hover:border-red-300"
                          onClick={() => handleDisconnect(integration)}
                          title={`Disconnect ${integration.name} integration`}
                        >
                          <Unplug className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Plus className="w-8 h-8 text-indigo-600" />
              </div>
              <h3 className="text-lg font-medium text-slate-900 mb-2">{t('connectedIntegrations.emptyTitle', { ns: 'integrations' })}</h3>
              <p className="text-slate-600 mb-4 max-w-md mx-auto">
                {t('connectedIntegrations.emptyDescription', { ns: 'integrations' })}
              </p>
            </div>
          </motion.div>
        )}

        {/* Filters and Search */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <Input
                  placeholder={t('page.searchPlaceholder', { ns: 'integrations' }) as string}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 border-slate-200 focus:border-indigo-500 focus:ring-indigo-500"
                />
              </div>
            </div>
            
            <Select value={selectedCategory} onValueChange={(value) => setSelectedCategory(value as any)}>
              <SelectTrigger className="w-48 border-slate-200">
                <SelectValue placeholder={t('page.category', { ns: 'integrations' })} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('page.allCategories', { ns: 'integrations' })}</SelectItem>
                <SelectItem value="ai_assistants">{t('page.categories.aiAssistants', { ns: 'integrations' })}</SelectItem>
                <SelectItem value="communication">{t('page.categories.communication', { ns: 'integrations' })}</SelectItem>
                <SelectItem value="automation">{t('page.categories.automation', { ns: 'integrations' })}</SelectItem>
                <SelectItem value="productivity">{t('page.categories.productivity', { ns: 'integrations' })}</SelectItem>
                <SelectItem value="development">{t('page.categories.development', { ns: 'integrations' })}</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={selectedStatus} onValueChange={(value) => setSelectedStatus(value as any)}>
              <SelectTrigger className="w-48 border-slate-200">
                <SelectValue placeholder={t('page.status', { ns: 'integrations' })} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('page.allStatus', { ns: 'integrations' })}</SelectItem>
                <SelectItem value="available">{t('page.statuses.available', { ns: 'integrations' })}</SelectItem>
                <SelectItem value="release_1_4">{t('page.statuses.release1_4', { ns: 'integrations' })}</SelectItem>
                <SelectItem value="in_progress">{t('page.statuses.inProgress', { ns: 'integrations' })}</SelectItem>
                <SelectItem value="in_development">{t('page.statuses.inDevelopment', { ns: 'integrations' })}</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={memoryTypeFilter}
              onValueChange={(value) =>
                setMemoryTypeFilter(value as 'all' | 'audio_supported')
              }
            >
              <SelectTrigger className="w-52 border-slate-200">
                <SelectValue placeholder={t('memoryModalities.filterLabel', { ns: 'integrations' })} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('memoryModalities.filterAll', { ns: 'integrations' })}</SelectItem>
                <SelectItem value="audio_supported">
                  {t('memoryModalities.filterAudioSupported', { ns: 'integrations' })}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Audio / feature support matrix (discoverability) */}
        <div className="mb-8">
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="audio-support" className="rounded-lg border border-border bg-card">
              <AccordionTrigger className="px-6 py-4 hover:no-underline">
                <div className="flex items-center gap-2">
                  <Info className="w-5 h-5 text-slate-500" />
                  <span className="text-sm font-medium text-slate-700">
                    {t('audioSupport.title', { ns: 'integrations' })}
                  </span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-6">
                <FeatureSupportMatrix feature="audio" />
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>

        {/* Integration Sections */}
        <div className="space-y-8">
          {renderIntegrationSection(
            t('page.sections.readyToConnect', { ns: 'integrations' }) as string,
            integrationsByStatus.available,
            integrationsByStatus.available.length
          )}

          {renderIntegrationSection(
            t('page.sections.inDevelopment', { ns: 'integrations' }) as string,
            [
              ...integrationsByStatus.release_1_4,
              ...integrationsByStatus.in_progress,
              ...integrationsByStatus.in_development,
            ],
            integrationsByStatus.release_1_4.length +
              integrationsByStatus.in_progress.length +
              integrationsByStatus.in_development.length
          )}
        </div>

        {filteredIntegrations.length === 0 && (
          <div className="text-center py-12">
            <div className="text-slate-400 mb-4">
              <Search className="w-12 h-12 mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-slate-900 mb-2">{t('page.noIntegrationsFound', { ns: 'integrations' })}</h3>
            <p className="text-slate-600">
              {t('page.tryAdjustingSearch', { ns: 'integrations' })}
            </p>
          </div>
        )}
      </div>

      {/* Modals */}
      {selectedIntegration && (
        <ComingSoonModal
          isOpen={showComingSoonModal}
          onClose={() => {
            setShowComingSoonModal(false);
            setSelectedIntegration(null);
          }}
          integration={selectedIntegration}
        />
      )}

      {/* Integration-specific Configuration Modals */}
      {(() => {
        const shouldRenderClaude = selectedIntegration?.id === 'claude_desktop' && showIntegrationConfigModal && currentSpaceId;
        if (selectedIntegration?.id === 'claude_desktop') {
          console.log('🎯 Claude Desktop modal render check:', {
            integrationId: selectedIntegration?.id,
            showModal: showIntegrationConfigModal,
            hasSpaceId: !!currentSpaceId,
            spaceId: currentSpaceId,
            shouldRender: shouldRenderClaude
          });
        }
        return shouldRenderClaude;
      })() && (
        <ClaudeDesktopIntegrationModal
          isOpen={showIntegrationConfigModal}
          onClose={() => {
            setShowIntegrationConfigModal(false);
            setSelectedIntegration(null);
            // Reload connected integrations after configuration
            if (currentSpaceId) {
              loadConnectedIntegrations();
            }
          }}
          onCreateIntegration={async (integrationData) => {
            // Actually create the integration binding via API
            console.log('Creating Claude Desktop integration binding:', integrationData);
            try {
              const socketId = integrationData.socket_id;
              if (!socketId) {
                throw new Error('Socket ID is required');
              }
              const { socket_id, ...dataToSend } = integrationData;
              const createdBinding = await apiService.createIntegrationBinding(socketId, dataToSend);
              console.log('Claude Desktop integration binding created:', createdBinding);
              
              // DON'T close the modal here - let the modal handle its own success flow
              // The modal will show the success screen with configuration steps and close itself when the user is done
              
              return createdBinding;
            } catch (error) {
              console.error('Failed to create Claude Desktop integration binding:', error);
              throw error; // Let the modal handle the error display
            }
          }}
          spaceId={currentSpaceId}
        />
      )}

      {selectedIntegration?.id === 'custom_gpt_action' && showIntegrationConfigModal && currentSpaceId && (
        <CustomGPTIntegrationModal
          isOpen={showIntegrationConfigModal}
          onClose={() => {
            setShowIntegrationConfigModal(false);
            setSelectedIntegration(null);
            // Reload connected integrations after configuration
            if (currentSpaceId) {
              loadConnectedIntegrations();
            }
          }}
          onCreateIntegration={async (integrationData) => {
            // Actually create the integration binding via API
            console.log('Creating Custom GPT integration binding:', integrationData);
            try {
              const socketId = integrationData.socket_id;
              if (!socketId) {
                throw new Error('Socket ID is required');
              }
              const { socket_id, ...dataToSend } = integrationData;
              const createdBinding = await apiService.createIntegrationBinding(socketId, dataToSend);
              console.log('Custom GPT integration binding created:', createdBinding);
              
              // DON'T close the modal here - let the modal handle its own success flow
              // The modal will show the success screen with configuration steps and close itself when the user is done
              
              return createdBinding;
            } catch (error) {
              console.error('Failed to create Custom GPT integration binding:', error);
              throw error; // Let the modal handle the error display
            }
          }}
          spaceId={currentSpaceId}
        />
      )}

      {selectedIntegration?.id === 'whatsapp_basic' && showIntegrationConfigModal && currentSpaceId && (
        <WhatsAppIntegrationModal
          isOpen={showIntegrationConfigModal}
          onClose={() => {
            setShowIntegrationConfigModal(false);
            setSelectedIntegration(null);
            setConfigIntegrationData(null);
            // Reload connected integrations after configuration
            if (currentSpaceId) {
              loadConnectedIntegrations();
            }
          }}
          onCreateIntegration={async (integrationData) => {
            console.log('Creating WhatsApp Basic integration:', integrationData);
            try {
              const socketId = integrationData.socket_id;
              if (!socketId) {
                throw new Error('Socket ID is required');
              }
              const { socket_id, ...dataToSend } = integrationData;
              const createdIntegration = await apiService.createIntegrationBinding(socketId, dataToSend);
              console.log('WhatsApp Basic integration created:', createdIntegration);
              if (currentSpaceId) {
                loadConnectedIntegrations();
              }
              return createdIntegration;
            } catch (error) {
              console.error('Failed to create WhatsApp Basic integration:', error);
              throw error;
            }
          }}
          spaceId={currentSpaceId}
          integrationId={selectedIntegration.id}
          integrationName={selectedIntegration.name}
          userTier={userTier}
          existingIntegration={configIntegrationData ? {
            bindingId: configIntegrationData.bindingId,
            binding: configIntegrationData.binding,
            socket: configIntegrationData.socket
          } : undefined}
        />
      )}

      {selectedIntegration?.id === 'chrome_extension' && showIntegrationConfigModal && currentSpaceId && (
        <ChromeExtensionIntegrationModal
          isOpen={showIntegrationConfigModal}
          onClose={() => {
            setShowIntegrationConfigModal(false);
            setSelectedIntegration(null);
            setConfigIntegrationData(null);
            // Reload connected integrations after configuration
            if (currentSpaceId) {
              loadConnectedIntegrations();
            }
          }}
          onCreateIntegration={async (integrationData) => {
            console.log('Creating Chrome Extension integration:', integrationData);
            try {
              // Call the backend API to create the integration binding
              const socketId = integrationData.socket_id;
              if (!socketId) {
                throw new Error('Socket ID is required');
              }
              const { socket_id, ...dataToSend } = integrationData;
              const createdIntegration = await apiService.createIntegrationBinding(socketId, dataToSend);
              console.log('Chrome Extension integration created:', createdIntegration);
              
              // DON'T close the modal here - let the modal handle its own success flow
              // The modal will show the success screen and close itself when the user is done
              
              return createdIntegration;
            } catch (error) {
              console.error('Failed to create Chrome Extension integration:', error);
              throw error; // Let the modal handle the error display
            }
          }}
          spaceId={currentSpaceId}
          existingIntegration={configIntegrationData ? {
            binding_id: configIntegrationData.bindingId,
            socket_id: configIntegrationData.socket.socket_id,
            integration_name: configIntegrationData.binding.integration_name,
            auth_config: configIntegrationData.binding.auth_config
          } : undefined}
        />
      )}

      {selectedIntegration?.id === 'bare_api' && showIntegrationConfigModal && currentSpaceId && (
        <BareAPIIntegrationModal
          isOpen={showIntegrationConfigModal}
          onClose={() => {
            setShowIntegrationConfigModal(false);
            setSelectedIntegration(null);
            // Reload connected integrations after configuration
            if (currentSpaceId) {
              loadConnectedIntegrations();
            }
          }}
          onCreateIntegration={async (integrationData) => {
            console.log('Creating Bare API integration binding:', integrationData);
            try {
              const socketId = integrationData.socket_id;
              if (!socketId) {
                throw new Error('Socket ID is required');
              }
              const { socket_id, ...dataToSend } = integrationData;
              const createdBinding = await apiService.createIntegrationBinding(socketId, dataToSend);
              console.log('Bare API integration binding created:', createdBinding);
              
              // DON'T close the modal here - let the modal handle its own success flow
              // The modal will show the success screen with configuration steps and close itself when the user is done
              
              return createdBinding;
            } catch (error) {
              console.error('Failed to create Bare API integration binding:', error);
              throw error; // Let the modal handle the error display
            }
          }}
          spaceId={currentSpaceId}
        />
      )}

      {selectedIntegration?.id === 'bare_mcp' && showIntegrationConfigModal && currentSpaceId && (
        <BareMCPIntegrationModal
          isOpen={showIntegrationConfigModal}
          onClose={() => {
            setShowIntegrationConfigModal(false);
            setSelectedIntegration(null);
            // Reload connected integrations after configuration
            if (currentSpaceId) {
              loadConnectedIntegrations();
            }
          }}
          onCreateIntegration={async (integrationData) => {
            console.log('Creating Bare MCP integration binding:', integrationData);
            try {
              const socketId = integrationData.socket_id;
              if (!socketId) {
                throw new Error('Socket ID is required');
              }
              const { socket_id, ...dataToSend } = integrationData;
              const createdBinding = await apiService.createIntegrationBinding(socketId, dataToSend);
              console.log('Bare MCP integration binding created:', createdBinding);
              
              // DON'T close the modal here - let the modal handle its own success flow
              // The modal will show the success screen with configuration steps and close itself when the user is done
              
              return createdBinding;
            } catch (error) {
              console.error('Failed to create Bare MCP integration binding:', error);
              throw error; // Let the modal handle the error display
            }
          }}
          spaceId={currentSpaceId}
        />
      )}

      {selectedIntegration?.id === 'cursor' && showIntegrationConfigModal && currentSpaceId && (
        <CursorIntegrationModal
          isOpen={showIntegrationConfigModal}
          onClose={() => {
            setShowIntegrationConfigModal(false);
            setSelectedIntegration(null);
            // Reload connected integrations after configuration
            if (currentSpaceId) {
              loadConnectedIntegrations();
            }
          }}
          onSuccess={async (integrationData) => {
            console.log('Creating Cursor integration binding:', integrationData);
            try {
              const socketId = integrationData.socket_id;
              if (!socketId) {
                throw new Error('Socket ID is required');
              }
              const { socket_id, ...dataToSend } = integrationData;
              const createdBinding = await apiService.createIntegrationBinding(socketId, dataToSend);
              console.log('Cursor integration binding created:', createdBinding);
              
              // DON'T close the modal here - let the modal handle its own flow
              // The modal shows an alert and handles its own close
              
              return createdBinding;
            } catch (error) {
              console.error('Failed to create Cursor integration binding:', error);
              throw error; // Let the modal handle the error display
            }
          }}
          spaceId={currentSpaceId}
        />
      )}

      {selectedIntegration?.id === 'n8n' && showIntegrationConfigModal && currentSpaceId && (
        <N8NIntegrationModal
          isOpen={showIntegrationConfigModal}
          onClose={() => {
            setShowIntegrationConfigModal(false);
            setSelectedIntegration(null);
            // Reload connected integrations after configuration
            if (currentSpaceId) {
              loadConnectedIntegrations();
            }
          }}
          onCreateIntegration={async (integrationData) => {
            console.log('Creating N8N integration binding:', integrationData);
            try {
              const socketId = integrationData.socket_id;
              if (!socketId) {
                throw new Error('Socket ID is required');
              }
              const { socket_id, ...dataToSend } = integrationData;
              const createdBinding = await apiService.createIntegrationBinding(socketId, dataToSend);
              console.log('N8N integration binding created:', createdBinding);
              
              // DON'T close the modal here - let the modal handle its own success flow
              // The modal will show the success screen with configuration steps and close itself when the user is done
              
              // Add space_id for modal compatibility
              return {
                ...createdBinding,
                space_id: currentSpaceId,
              };
            } catch (error) {
              console.error('Failed to create N8N integration binding:', error);
              throw error; // Let the modal handle the error display
            }
          }}
          spaceId={currentSpaceId}
        />
      )}

      {selectedIntegration?.id === 'dify' && showIntegrationConfigModal && currentSpaceId && (
        <DifyIntegrationModal
          isOpen={showIntegrationConfigModal}
          onClose={() => {
            setShowIntegrationConfigModal(false);
            setSelectedIntegration(null);
            if (currentSpaceId) {
              loadConnectedIntegrations();
            }
          }}
          onCreateIntegration={async (integrationData) => {
            try {
              const socketId = integrationData.socket_id;
              if (!socketId) {
                throw new Error('Socket ID is required');
              }
              const { socket_id, ...dataToSend } = integrationData;
              const createdBinding = await apiService.createIntegrationBinding(
                socketId,
                dataToSend
              );
              return {
                ...createdBinding,
                space_id: currentSpaceId,
              };
            } catch (error) {
              console.error('Failed to create Dify integration binding:', error);
              throw error;
            }
          }}
          spaceId={currentSpaceId}
        />
      )}

      {selectedIntegration?.id === 'twitter_bookmarks' && showIntegrationConfigModal && currentSpaceId && (
        <XBookmarksIntegrationModal
          isOpen={showIntegrationConfigModal}
          onClose={() => {
            setShowIntegrationConfigModal(false);
            setSelectedIntegration(null);
            setConfigIntegrationData(null);
            if (currentSpaceId) {
              loadConnectedIntegrations();
            }
          }}
          onCreateIntegration={async (integrationData: any) => {
            try {
              const socketId = integrationData.socket_id;
              if (!socketId) {
                throw new Error('Socket ID is required');
              }
              const { socket_id, ...dataToSend } = integrationData;
              const createdBinding = await apiService.createIntegrationBinding(
                socketId,
                dataToSend
              );
              return createdBinding;
            } catch (error) {
              console.error('Failed to create X bookmarks integration binding:', error);
              throw error;
            }
          }}
          spaceId={currentSpaceId}
          existingIntegration={configIntegrationData ?? undefined}
        />
      )}

      {selectedIntegration?.id === 'zapier' && showIntegrationConfigModal && currentSpaceId && (
        <ZapierIntegrationModal
          isOpen={showIntegrationConfigModal}
          onClose={() => {
            setShowIntegrationConfigModal(false);
            setSelectedIntegration(null);
            // Reload connected integrations after configuration
            if (currentSpaceId) {
              loadConnectedIntegrations();
            }
          }}
          onCreateIntegration={async (integrationData) => {
            console.log('Creating Zapier integration binding:', integrationData);
            try {
              const socketId = integrationData.socket_id;
              if (!socketId) {
                throw new Error('Socket ID is required');
              }
              const { socket_id, ...dataToSend } = integrationData;
              const createdBinding = await apiService.createIntegrationBinding(socketId, dataToSend);
              console.log('Zapier integration binding created:', createdBinding);
              
              // DON'T close the modal here - let the modal handle its own success flow
              // The modal will show the success screen with configuration steps and close itself when the user is done
              
              // Add space_id for modal compatibility
              return {
                ...createdBinding,
                space_id: currentSpaceId,
              };
            } catch (error) {
              console.error('Failed to create Zapier integration binding:', error);
              throw error; // Let the modal handle the error display
            }
          }}
          spaceId={currentSpaceId}
        />
      )}

      {/* Generic Configuration Modal */}
      {showConfigModal && configIntegrationId && configIntegrationData && (
        <IntegrationConfigurationModal
          integrationId={configIntegrationId}
          isOpen={showConfigModal}
          onClose={() => {
            setShowConfigModal(false);
            setConfigIntegrationId('');
            setConfigIntegrationData(null);
            // Reload connected integrations after configuration
            if (currentSpaceId) {
              loadConnectedIntegrations();
            }
          }}
          integrationData={configIntegrationData}
          spaceName={currentSpace?.name || t('page.spaceNameFallback', { ns: 'integrations' })}
        />
      )}
    </div>
  );
} 